import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Marks a section as revealed the first time it scrolls into view, so content
 * below the fold arrives as one piece instead of snapping in.
 *
 * Content is revealed immediately when the platform asks for reduced motion or
 * when IntersectionObserver is unavailable, so nothing can be left hidden.
 */
export default function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setIsRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsRevealed(true);
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isRevealed];
}
